import { useState, useEffect } from "react";
import {
  Typography, Form, Input, Select, DatePicker, Button, Card,
  Row, Col, Space, Empty, Spin, Alert, Tag, Divider, Pagination, Grid,
} from "antd";
import { SearchOutlined, ClearOutlined, UserOutlined, FileTextOutlined, CalendarOutlined } from "@ant-design/icons";
import { searchRepertoryIndex, getRepertoryYears, getRepertoryActTypes, logPortalActivity } from "../../api/portalService";

const { useBreakpoint } = Grid;

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MONTHS = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const ACT_COLORS = {
  COMPRAVENTA: "green", MANDATO: "blue", PROMESA: "orange",
  CESION: "purple", CESIÓN: "purple", PROTOCOLIZACION: "default",
  PROTOCOLIZACIÓN: "default", TESTAMENTO: "red",
  SEPARACION: "magenta", LIQUIDACION: "magenta", PODER: "cyan",
  REDUCCION: "gold", REDUCCIÓN: "gold", CONSTITUCION: "lime",
  USUFRUCTO: "volcano", SERVIDUMBRE: "geekblue", HIPOTECA: "volcano",
};

function getActColor(actType) {
  if (!actType) return "default";
  const upper = actType.toUpperCase();
  for (const [key, color] of Object.entries(ACT_COLORS)) {
    if (upper.startsWith(key)) return color;
  }
  return "geekblue";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const ResultCard = ({ entry, isMobile }) => (
  <Card
    size="small"
    style={{ borderRadius: 8, border: "1px solid #e8e8e8", marginBottom: 8 }}
    bodyStyle={{ padding: isMobile ? "12px 14px" : "14px 20px" }}
  >
    {isMobile ? (
      /* Layout mobile: apilado */
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ textAlign: "center" }}>
              <Text strong style={{ fontSize: 22, color: "#1d3557", display: "block", lineHeight: 1.1 }}>
                {entry.repertoryNumber}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{entry.year}</Text>
            </div>
            <Divider type="vertical" style={{ height: 36, margin: "0 4px" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <CalendarOutlined style={{ color: "#aaa", fontSize: 11 }} />
              <Text style={{ fontSize: 12 }}>{formatDate(entry.date)}</Text>
            </div>
          </div>
          {entry.actType && (
            <Tag
              color={getActColor(entry.actType)}
              style={{ fontSize: 10, whiteSpace: "normal", lineHeight: "1.3", padding: "2px 6px", maxWidth: 130, textAlign: "left" }}
            >
              {entry.actType}
            </Tag>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
          <UserOutlined style={{ color: "#aaa", fontSize: 12, marginTop: 2, flexShrink: 0 }} />
          <div>
            <Text style={{ fontSize: 13, fontWeight: 500, display: "block" }}>
              {entry.party1 || entry.rawOtorgantes}
            </Text>
            {entry.party2 && (
              <Text type="secondary" style={{ fontSize: 12 }}>/ {entry.party2}</Text>
            )}
          </div>
        </div>
        {entry.docProtRef && (
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
            {entry.docProtRef}
          </Text>
        )}
      </div>
    ) : (
      /* Layout desktop: horizontal */
      <Row gutter={0} align="middle" wrap={false}>
        <Col flex="72px" style={{ textAlign: "center", flexShrink: 0 }}>
          <Text strong style={{ fontSize: 20, color: "#1d3557", display: "block", lineHeight: 1.2 }}>
            {entry.repertoryNumber}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{entry.year}</Text>
        </Col>
        <Divider type="vertical" style={{ height: 44, margin: "0 14px" }} />
        <Col flex="100px" style={{ flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <CalendarOutlined style={{ color: "#aaa", fontSize: 12 }} />
            <Text style={{ fontSize: 13 }}>{formatDate(entry.date)}</Text>
          </div>
        </Col>
        <Divider type="vertical" style={{ height: 44, margin: "0 14px" }} />
        <Col flex="1" style={{ minWidth: 0, paddingRight: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
            <UserOutlined style={{ color: "#aaa", fontSize: 13, marginTop: 3, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <Text style={{ fontSize: 13, fontWeight: 500, display: "block" }}>
                {entry.party1 || entry.rawOtorgantes}
              </Text>
              {entry.party2 && (
                <Text type="secondary" style={{ fontSize: 12 }}>/ {entry.party2}</Text>
              )}
            </div>
          </div>
        </Col>
        <Col flex="0 0 auto" style={{ maxWidth: 220, textAlign: "right" }}>
          {entry.actType ? (
            <Tag
              color={getActColor(entry.actType)}
              style={{ fontSize: 11, whiteSpace: "normal", lineHeight: "1.35", padding: "2px 8px", textAlign: "left" }}
            >
              {entry.actType}
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
          )}
          {entry.docProtRef && (
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 3 }}>
              {entry.docProtRef}
            </Text>
          )}
        </Col>
      </Row>
    )}
  </Card>
);

const PortalSearchIndex = () => {
  const screens  = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [years, setYears] = useState([]);
  const [actTypes, setActTypes] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [lastValues, setLastValues] = useState({});

  useEffect(() => {
    getRepertoryYears().then(({ data }) => setYears(data.years || [])).catch(() => {});
    getRepertoryActTypes().then(({ data }) => setActTypes(data.actTypes || [])).catch(() => {});
  }, []);

  const buildParams = (values, currentPage) => {
    const p = { page: currentPage, limit: 25 };
    if (values.year) p.year = values.year;
    if (values.month) p.month = values.month;
    if (values.repertoryNumber) p.repertoryNumber = values.repertoryNumber;
    if (values.partyName) p.partyName = values.partyName;
    if (values.actType) p.actType = values.actType;
    if (values.dateRange?.[0]) p.dateFrom = values.dateRange[0].startOf("day").toISOString();
    if (values.dateRange?.[1]) p.dateTo = values.dateRange[1].endOf("day").toISOString();
    return p;
  };

  const doSearch = async (values, currentPage = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams(values, currentPage);
      const { data } = await searchRepertoryIndex(params);
      setResults(data.results || []);
      setTotal(data.totalResults || 0);
      setPage(currentPage);
      setSearched(true);
      if (currentPage === 1) logPortalActivity('search', { ...params, totalResults: data.totalResults || 0 });
    } catch {
      setError("Error al realizar la búsqueda.");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    setLastValues(values);
    doSearch(values, 1);
  };

  const onReset = () => {
    form.resetFields();
    setResults([]);
    setTotal(0);
    setSearched(false);
    setPage(1);
    setLastValues({});
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0, color: "#1d3557" }}>
          Buscar Índice de Escrituras
        </Title>
        <Text type="secondary">
          Busca en el índice de escrituras públicas y documentos protocolizados de la notaría.
        </Text>
      </div>

      {/* Formulario */}
      <Card style={{ borderRadius: 10, marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[12, 0]}>
            {/* Fila 1: Año | Mes | N° Rep | Nombre */}
            <Col xs={12} sm={6} md={5}>
              <Form.Item label="Año" name="year" style={{ marginBottom: 14 }}>
                <Select placeholder="Todos" allowClear>
                  {years.map(y => <Option key={y} value={y}>{y}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={5}>
              <Form.Item label="Mes" name="month" style={{ marginBottom: 14 }}>
                <Select placeholder="Todos" allowClear>
                  {MONTHS.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={4} md={4}>
              <Form.Item label="N° Repertorio" name="repertoryNumber" style={{ marginBottom: 14 }}>
                <Input placeholder="Ej: 278" type="number" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8} md={10}>
              <Form.Item label="Nombre otorgante" name="partyName" style={{ marginBottom: 14 }}>
                <Input
                  placeholder="Ej: GONZALEZ, JUAN"
                  prefix={<UserOutlined style={{ color: "#ccc" }} />}
                />
              </Form.Item>
            </Col>

            {/* Fila 2: Tipo de acto | Rango de fechas */}
            <Col xs={24} sm={12} md={10}>
              <Form.Item label="Tipo de acto" name="actType" style={{ marginBottom: 14 }}>
                <Select
                  placeholder="Selecciona o escribe un tipo..."
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  suffixIcon={<FileTextOutlined style={{ color: "#ccc" }} />}
                >
                  {actTypes.map(t => (
                    <Option key={t} value={t}>{t}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {!isMobile && (
              <Col xs={24} sm={12} md={14}>
                <Form.Item label="Rango de fechas" name="dateRange" style={{ marginBottom: 14 }}>
                  <RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder={["Desde", "Hasta"]}
                    separator="→"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Space style={{ width: isMobile ? "100%" : "auto" }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              block={isMobile}
              style={{ background: "#1d3557", borderColor: "#1d3557", borderRadius: 6, height: isMobile ? 42 : 32 }}
            >
              Buscar
            </Button>
            <Button icon={<ClearOutlined />} onClick={onReset} style={{ borderRadius: 6, height: isMobile ? 42 : 32 }}>
              Limpiar
            </Button>
          </Space>
        </Form>
      </Card>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Resultados */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#999" }}>Buscando...</div>
        </div>
      ) : searched && (
        <div>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 12, padding: "0 2px",
          }}>
            <Text style={{ color: "#555", fontSize: 14 }}>
              {total === 0
                ? "Sin resultados"
                : <><Text strong>{total.toLocaleString()}</Text> resultado{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}</>
              }
            </Text>
            {total > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Página {page} de {Math.ceil(total / 25)}
              </Text>
            )}
          </div>

          {results.length === 0 ? (
            <Empty
              description={
                <span>
                  No se encontraron registros.<br />
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Intenta con otro nombre, año o tipo de acto.
                  </Text>
                </span>
              }
              style={{ padding: 40 }}
            />
          ) : (
            <>
              {/* Header — solo desktop */}
              {!isMobile && (
                <div style={{
                  display: "flex", padding: "2px 20px 6px",
                  color: "#aaa", fontSize: 11, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>
                  <div style={{ width: 72, textAlign: "center" }}>Rep. / Año</div>
                  <div style={{ width: 42 }} />
                  <div style={{ width: 100 }}>Fecha</div>
                  <div style={{ width: 28 }} />
                  <div style={{ flex: 1 }}>Otorgantes</div>
                  <div style={{ width: 220, textAlign: "right" }}>Acto</div>
                </div>
              )}

              {results.map((entry) => (
                <ResultCard
                  key={entry.id || `${entry.year}-${entry.repertoryNumber}-${entry.party1}`}
                  entry={entry}
                  isMobile={isMobile}
                />
              ))}

              {total > 25 && (
                <div style={{ textAlign: "center", marginTop: 20 }}>
                  <Pagination
                    current={page}
                    pageSize={25}
                    total={total}
                    showSizeChanger={false}
                    onChange={(p) => doSearch(lastValues, p)}
                    showTotal={(t) => `${t.toLocaleString()} registros`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSearchIndex;
